import { Component, inject, Input, TemplateRef } from '@angular/core';
import { Params } from '@angular/router';

import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { InternationalizationService } from '@rx-applications/clientsite/shared/internationalization/data-access';

import { RouterFacade } from '@rx-applications/clientsite/shared/router/data-access';
import { TRANSLOCO_SCOPE, TranslocoDirective, TranslocoService } from '@ngneat/transloco';
import { inlineLoaderFactory } from '@rx-applications/shared/utils';
import { IconSvgComponent, RxButtonModule } from '@roomex/rx-components';
import { AsyncPipe, DatePipe, NgIf, NgTemplateOutlet } from '@angular/common';
import { OccupancyShorthandComponent } from '../components/occupancy-shorthand/occupancy-shorthand.component';
import { SearchType } from '@rx-applications/clientsite/search/searchbox/data-access';
import { LoggingFacade } from '@rx-applications/clientsite/core/logging';

enum Occupancy {
  Room = 'roomSelect.roomDescSingle',
  Rooms = 'roomSelect.roomDescPlural',
  Adult = 'roomSelect.adultDescSingle',
  Adults = 'roomSelect.adultDescPlural',
}

interface OccupancyTranslations {
  room: string;
  rooms: string;
  adult: string;
  adults: string;
}

@Component({
  selector: 'rx-applications-search-panel',
  standalone: true,
  imports: [
    IconSvgComponent,
    RxButtonModule,
    TranslocoDirective,
    NgTemplateOutlet,
    AsyncPipe,
    DatePipe,
    OccupancyShorthandComponent,
    NgIf,
  ],
  templateUrl: './feature-search-summary-panel.component.html',
  styleUrl: './feature-search-summary-panel.component.scss',
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: {
        scope: 'searchPanel',
        loader: inlineLoaderFactory((lang: string) => import(`./assets/i18n/${lang}.json`)),
      },
    },
  ],
})
export class FeatureSearchSummaryPanelComponent {
  readonly #internationalizationService = inject(InternationalizationService);
  @Input() searchBoxRef: TemplateRef<void>;
  @Input() searchSelectedOptions: Params;
  public dateFormat: string = this.#internationalizationService.datePickerFormat;
  private modalOptions: NgbModalOptions = { windowClass: 'edit-search-modal' };

  readonly searchBoxType = SearchType.ResultsModal;

  constructor(
    public routerFacade: RouterFacade,
    private modalService: NgbModal,
    private transloco: TranslocoService,
    private loggingFacade: LoggingFacade,
  ) {}

  get searchedName() {
    if (this.searchSelectedOptions) {
      return this.searchSelectedOptions['hotelName'] || this.searchSelectedOptions['locationName'];
    }
    return '';
  }

  get occupancyTranslations$(): Observable<OccupancyTranslations> {
    return this.transloco.selectTranslate(Object.values(Occupancy), {}, 'searchPanel').pipe(
      take(1),
      map(([room, rooms, adult, adults]) => ({
        room,
        rooms,
        adult,
        adults,
      })),
    );
  }

  editSearch(content: TemplateRef<void>) {
    this.modalService.open(content, this.modalOptions);
    this.loggingFacade.editLocationSearchClicked();
  }
}
